import React from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  HelpCircle, 
  Phone, 
  Calendar, 
  DollarSign, 
  Clock, 
  Shield,
  Home,
  Wrench,
  FileText,
  MapPin
} from 'lucide-react';

const FAQPage = () => {
  const faqCategories = [
    {
      title: "General Roofing Questions",
      icon: Home,
      questions: [
        {
          question: "How do I know if I need a new roof?",
          answer: "Signs you may need a new roof include: shingles that are curling, cracking, or missing; granules in gutters; leaks or water stains; sagging roof deck; or if your roof is over 20-25 years old. We offer free inspections to assess your roof's condition. For comprehensive guidelines, the National Roofing Contractors Association provides detailed homeowner resources."
        },
        {
          question: "How long does a typical roof last?",
          answer: "According to industry standards from the Asphalt Roofing Manufacturers Association, asphalt shingles typically last 20-30 years, metal roofs 40-70 years, and tile roofs can last 50+ years. Lifespan depends on material quality, installation, maintenance, and weather conditions in Eastern NC."
        },
        {
          question: "What's the best roofing material for North Carolina weather?",
          answer: "For Eastern NC's climate, we recommend architectural asphalt shingles, metal roofing, or synthetic materials based on North Carolina State University climate studies. These handle our humidity, storms, and temperature changes well. The U.S. Department of Energy also provides guidance on cool roofing options for energy efficiency."
        },
        {
          question: "Should I repair or replace my roof?",
          answer: "If damage is localized and your roof is under 15 years old, repairs may be sufficient. If you have widespread damage, multiple leaks, or an aging roof (20+ years), replacement is usually more cost-effective long-term. Consumer Reports provides detailed guidance on making this decision."
        }
      ]
    },
    {
      title: "Installation & Process",
      icon: Wrench,
      questions: [
        {
          question: "How long does a roof replacement take?",
          answer: "Most residential roof replacements take 1-3 days, depending on size, complexity, and weather. Larger or more complex roofs may take longer. We'll provide a specific timeline during your estimate."
        },
        {
          question: "What happens if it rains during installation?",
          answer: "We monitor weather closely using National Weather Service forecasts and use tarps and protective measures. If severe weather is predicted, we'll pause work and secure your roof. We never compromise on protecting your home from water damage."
        },
        {
          question: "Do I need to be home during installation?",
          answer: "You don't need to be home, but we recommend someone be available for questions and final walkthrough. We'll coordinate with you on timing and access needs."
        },
        {
          question: "How do you protect my landscaping and property?",
          answer: "We use tarps, plywood, and magnetic tools to protect landscaping, vehicles, and outdoor furniture. Our crew performs thorough cleanup daily, including magnet sweeps for nails and debris."
        }
      ]
    },
    {
      title: "Cost & Financing",
      icon: DollarSign,
      questions: [
        {
          question: "How much does a new roof cost?",
          answer: "Roof replacement costs vary based on size, materials, complexity, and local factors. In Eastern NC, typical ranges are $8,000-$25,000. We provide detailed, transparent estimates after our free inspection."
        },
        {
          question: "Do you offer financing options?",
          answer: "Yes! We offer multiple financing options including competitive rate personal loans and flexible payment terms. Most applications are approved quickly, often same-day."
        },
        {
          question: "What's included in your estimates?",
          answer: "Our estimates include all materials, labor, permits, disposal, cleanup, and warranty coverage. We provide detailed breakdowns with no hidden fees or surprise charges."
        },
        {
          question: "Do you provide free estimates?",
          answer: "Yes, we provide completely free, no-obligation estimates including thorough roof inspections. We'll assess your roof condition and provide detailed written recommendations."
        }
      ]
    },
    {
      title: "Insurance & Storm Damage",
      icon: Shield,
      questions: [
        {
          question: "Will insurance cover my roof replacement?",
          answer: "Insurance typically covers storm damage, sudden events, and qualifying wear issues according to Insurance Information Institute guidelines. We work directly with insurance companies, provide detailed documentation following FEMA standards, and help navigate the claims process."
        },
        {
          question: "How do I file an insurance claim for roof damage?",
          answer: "Contact your insurance company first, then call us. We'll document damage using professional methods, provide detailed reports meeting industry standards, meet with adjusters, and advocate for proper coverage of necessary repairs or replacement."
        },
        {
          question: "What if my insurance claim is denied?",
          answer: "We help appeal denied claims by providing additional documentation, expert assessments, and working with public adjusters when needed. Many initially denied claims are successfully appealed."
        },
        {
          question: "Do you work with all insurance companies?",
          answer: "Yes, we work with all major insurance providers in North Carolina. We understand their requirements and processes, making claims smoother for homeowners."
        }
      ]
    },
    {
      title: "Warranties & Service",
      icon: FileText,
      questions: [
        {
          question: "What warranties do you provide?",
          answer: "We provide comprehensive workmanship warranties starting at 5 years (up to 10 years on certain packages) plus manufacturer material warranties (20-50+ years depending on material). Certain warranty packages are transferable to new homeowners."
        },
        {
          question: "Do you provide emergency roof services?",
          answer: "Yes, we offer 24/7 emergency services for storm damage, leaks, and urgent repairs. We'll secure your property immediately and schedule permanent repairs quickly."
        },
        {
          question: "How do I maintain my new roof?",
          answer: "Regular maintenance includes: annual inspections, gutter cleaning, removing debris, checking flashing, and addressing minor issues promptly. The Asphalt Roofing Manufacturers Association provides comprehensive maintenance guidelines. We provide maintenance checklists and ongoing support based on industry best practices."
        },
        {
          question: "What areas do you serve?",
          answer: "We serve all of Eastern North Carolina including Rocky Mount, Greenville, Wilson, Tarboro, Zebulon, Raleigh area, and surrounding communities. Contact us to confirm service in your specific location."
        }
      ]
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Frequently Asked Questions | Carolina Trophy Roofs | Roofing FAQ</title>
        <meta name="description" content="Get answers to common roofing questions about installation, costs, insurance, warranties, and maintenance. Expert advice from Carolina Trophy Roofs serving Eastern NC." />
        <meta name="keywords" content="roofing FAQ, roof replacement questions, roofing costs, insurance claims, roof installation, Carolina Trophy Roofs" />
        <link rel="canonical" href="https://www.carolinatrophyroofs.com/frequently-asked-questions" />
        
        <meta property="og:title" content="Roofing FAQ | Carolina Trophy Roofs" />
        <meta property="og:description" content="Expert answers to your roofing questions about installation, costs, insurance, and maintenance." />
        <meta property="og:url" content="https://www.carolinatrophyroofs.com/frequently-asked-questions" />
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqCategories.flatMap(category => 
              category.questions.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            )
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-brand-charcoal-gray to-gray-800 text-white py-20 md:py-28">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&h=1080&fit=crop&auto=format&q=75" 
            alt="Roofing questions and answers" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative container mx-auto px-4 z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-brand-safety-orange text-white px-4 py-2 text-sm font-semibold">
              EXPERT ANSWERS
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-brand-trophy-gold">
              Frequently Asked Questions
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl mx-auto">
              Get expert answers to common roofing questions from Eastern NC's trusted roofing professionals
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold px-8 py-4">
                  <Calendar className="mr-2 h-5 w-5" />
                  Ask Our Experts
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold/10">
                <Phone className="mr-2 h-5 w-5" />
                Call (252) 302-2003
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Quick Links */}
          <div className="mb-16 text-center">
            <h2 className="text-2xl font-bold mb-8 text-brand-charcoal-gray">Quick Navigation</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {faqCategories.map((category, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="px-4 py-2 text-sm border-brand-trophy-gold text-brand-charcoal-gray hover:bg-brand-trophy-gold/10 cursor-pointer"
                  onClick={() => document.getElementById(`category-${index}`)?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <category.icon className="w-4 h-4 mr-2" />
                  {category.title}
                </Badge>
              ))}
            </div>
          </div>

          {/* FAQ Categories */}
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} id={`category-${categoryIndex}`} className="mb-12">
              <Card className="border-2 border-gray-200">
                <CardHeader className="bg-gradient-to-r from-gray-50 to-white">
                  <CardTitle className="flex items-center text-2xl font-bold text-brand-charcoal-gray">
                    <div className="w-10 h-10 bg-brand-trophy-gold/10 rounded-full flex items-center justify-center mr-4">
                      <category.icon className="h-6 w-6 text-brand-trophy-gold" />
                    </div>
                    {category.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Accordion type="single" collapsible className="w-full">
                    {category.questions.map((faq, faqIndex) => (
                      <AccordionItem key={faqIndex} value={`${categoryIndex}-${faqIndex}`} className="border-gray-200">
                        <AccordionTrigger className="px-6 py-4 text-left hover:bg-gray-50 font-semibold text-brand-charcoal-gray">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-4 text-gray-700 leading-relaxed">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          ))}

          {/* Still Have Questions Section */}
          <Card className="bg-gradient-to-r from-brand-charcoal-gray to-gray-800 text-white mt-16">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="w-16 h-16 bg-brand-trophy-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <HelpCircle className="h-8 w-8 text-brand-trophy-gold" />
              </div>
              <h2 className="text-3xl font-bold mb-4 text-brand-trophy-gold">
                Still Have Questions?
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-200">
                Our roofing experts are here to help. Get personalized answers to your specific roofing questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold px-8 py-4">
                    <Calendar className="mr-2 h-5 w-5" />
                    Schedule Consultation
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold/10">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (252) 302-2003
                </Button>
              </div>
              <div className="mt-8 text-sm text-gray-300">
                <p>✓ Free consultations • ✓ Expert advice • ✓ No pressure sales</p>
              </div>
            </CardContent>
          </Card>

          {/* Service Area Note */}
          <div className="mt-12 text-center">
            <div className="flex items-center justify-center mb-4">
              <MapPin className="h-5 w-5 text-brand-safety-orange mr-2" />
              <span className="font-semibold text-brand-charcoal-gray">Service Areas</span>
            </div>
            <p className="text-gray-600">
              Carolina Trophy Roofs proudly serves Rocky Mount, Greenville, Wilson, Tarboro, Zebulon, 
              and all of Eastern North Carolina with expert roofing services.
            </p>
          </div>

          {/* SEO Resources & Citations */}
          <Card className="mt-16 border-2 border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-white">
              <CardTitle className="flex items-center text-2xl font-bold text-brand-charcoal-gray">
                <div className="w-10 h-10 bg-brand-trophy-gold/10 rounded-full flex items-center justify-center mr-4">
                  <FileText className="h-6 w-6 text-brand-trophy-gold" />
                </div>
                Industry Resources & References
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-600 mb-6">
                Our roofing expertise is backed by industry-leading organizations and authoritative sources. 
                These resources provide additional information to help you make informed decisions about your roofing needs.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-brand-charcoal-gray mb-3">Industry Standards & Guidelines</h4>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://www.nrca.net/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        National Roofing Contractors Association - Homeowner's Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://www.asphaltroofing.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Asphalt Roofing Manufacturers Association - Maintenance Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://www.consumerreports.org/cro/roofing/buying-guide/index.htm" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Consumer Reports - Roofing Warranties & Buying Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://www.bbb.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Better Business Bureau - Contractor Standards
                      </a>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-brand-charcoal-gray mb-3">Government & Educational Resources</h4>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://www.iii.org/article/how-much-homeowners-insurance-do-you-need" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Insurance Information Institute - Property Insurance Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://www.fema.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        FEMA - Roof Damage Documentation Guidelines
                      </a>
                    </li>
                    <li>
                      <a href="https://www.energy.gov/energysaver/cool-roofs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        U.S. Department of Energy - Cool Roofs Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://climate.ncsu.edu/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        NC State University - Climate Impact Studies
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-6">
                <h4 className="font-semibold text-brand-charcoal-gray mb-3">Specialized Services & Weather Preparedness</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://www.nachi.org/infrared-inspections.htm" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        InterNACHI - Thermal Imaging Inspection Standards
                      </a>
                    </li>
                    <li>
                      <a href="https://www.weather.gov/safety/thunderstorm" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        National Weather Service - Severe Weather Preparedness
                      </a>
                    </li>
                  </ul>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://www.epa.gov/heatisland/cool-roofs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        EPA - Building Efficiency & Cool Roofs
                      </a>
                    </li>
                    <li>
                      <a href="https://www.nationalroofingdirectory.org/united-states/rocky-mount/roofing-1/carolina-trophy-roofs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        National Roofing Directory - Carolina Trophy Roofs Recognition
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-gray-700">
                  <strong>Disclaimer:</strong> These external resources are provided for educational purposes. 
                  Carolina Trophy Roofs is not responsible for the content of external websites. 
                  Always consult with our certified professionals for specific roofing advice tailored to your situation.
                </p>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </Layout>
  );
};

export default FAQPage;